import React from 'react';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Textarea } from '../../../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../../../ui/card';
import { Label } from '../../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { Loader2, Trash2 } from 'lucide-react';
import { useAuth } from '../../../../lib/auth/AuthContext';
import { Id } from '../../../../convex/_generated/dataModel';
import { ProjectOverviewContent, UnitType, SellingPoint, FileId } from '../../../../types/pageBuilder';
import { DEFAULT_PROJECT_OVERVIEW_CONTENT } from '../../../../constants/pageBuilderDefaults';
import { useToast } from '../../../ui/use-toast';
import { ImageUploader } from '../../../ui/image-uploader';

interface ProjectOverviewSectionProps {
  projectId: string;
  selectedLanguage: string;
  projectOverviewComponent: any;
  projectOverviewContent: ProjectOverviewContent;
  setProjectOverviewContent: React.Dispatch<React.SetStateAction<ProjectOverviewContent>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  refreshComponents: () => void;
}

export const ProjectOverviewSection: React.FC<ProjectOverviewSectionProps> = ({
  projectId,
  selectedLanguage,
  projectOverviewComponent,
  projectOverviewContent,
  setProjectOverviewContent,
  isLoading,
  setIsLoading,
  refreshComponents
}) => {
  const { useAuthenticatedMutation } = useAuth();
  const { toast } = useToast();
  const saveComponent = useAuthenticatedMutation('pageComponents:upsert');

  const handleSaveProjectOverviewComponent = async () => {
    if (!projectId || !selectedLanguage) {
      toast({
        title: 'Error',
        description: 'Project ID and language are required'
      });
      return;
    }

    console.log('Saving project overview component with data:', projectOverviewContent);
    setIsLoading(true);
    
    try {
      const componentId = await saveComponent({
        projectId: projectId as Id<"projects">,
        languageCode: selectedLanguage,
        componentType: 'projectOverview',
        order: 300, // Third component
        content: projectOverviewContent,
        isActive: true,
        componentId: projectOverviewComponent?._id
      });
      
      console.log('Project overview component saved with ID:', componentId);
      toast({
        title: 'Success',
        description: 'Project overview section saved successfully'
      });
      
      // Refresh the component list without changing the active tab
      refreshComponents();
    } catch (error) {
      console.error('Error saving project overview component:', error);
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to save project overview'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addSellingPoint = () => {
    setProjectOverviewContent(prev => ({
      ...prev,
      sellingPoints: [
        ...prev.sellingPoints,
        {
          icon: 'location',
          title: '',
          description: ''
        }
      ]
    }));
  };

  const removeSellingPoint = (index: number) => {
    setProjectOverviewContent(prev => ({
      ...prev,
      sellingPoints: prev.sellingPoints.filter((_, i) => i !== index)
    }));
  };

  const updateSellingPoint = (index: number, field: keyof SellingPoint, value: string) => {
    setProjectOverviewContent(prev => ({
      ...prev,
      sellingPoints: prev.sellingPoints.map((point, i) => 
        i === index ? { ...point, [field]: value } : point
      )
    }));
  };

  const addUnitType = () => {
    setProjectOverviewContent(prev => ({
      ...prev,
      unitTypes: [
        ...prev.unitTypes,
        {
          type: '',
          size: '',
          startingPrice: '',
          image: '',
          imageId: undefined
        }
      ]
    }));
  };

  const removeUnitType = (index: number) => {
    setProjectOverviewContent(prev => ({
      ...prev,
      unitTypes: prev.unitTypes.filter((_, i) => i !== index)
    }));
  };

  const updateUnitType = (index: number, field: keyof UnitType, value: string) => {
    setProjectOverviewContent(prev => ({
      ...prev,
      unitTypes: prev.unitTypes.map((unit, i) => 
        i === index ? { ...unit, [field]: value } : unit
      )
    }));
  };

  const handleUnitTypeImageChange = (index: number, fileId: FileId | null, imageUrl: string) => {
    setProjectOverviewContent(prev => ({
      ...prev,
      unitTypes: prev.unitTypes.map((unit, i) => 
        i === index ? { ...unit, image: imageUrl, imageId: fileId || undefined } : unit
      )
    }));
  };

  const resetToDefault = () => {
    setProjectOverviewContent(DEFAULT_PROJECT_OVERVIEW_CONTENT);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Project Overview</CardTitle>
        <CardDescription>
          Define the project overview section with key details and unit types
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label htmlFor="overview-title">Section Title</Label>
              <Input
                id="overview-title"
                value={projectOverviewContent.title}
                onChange={(e) => setProjectOverviewContent(prev => ({ ...prev, title: e.target.value }))}
                placeholder="The Grand Reserve"
              />
            </div>
            
            <div>
              <Label htmlFor="overview-status">Project Status</Label>
              <Select 
                value={projectOverviewContent.status} 
                onValueChange={(value) => setProjectOverviewContent(prev => ({ 
                  ...prev, 
                  status: value as "Off-Plan" | "Under Construction" | "Ready" 
                }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Off-Plan">Off-Plan</SelectItem>
                  <SelectItem value="Under Construction">Under Construction</SelectItem>
                  <SelectItem value="Ready">Ready</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="overview-description">Description</Label>
              <Textarea
                id="overview-description"
                value={projectOverviewContent.description}
                onChange={(e) => setProjectOverviewContent(prev => ({ ...prev, description: e.target.value }))}
                placeholder="An architectural masterpiece offering unparalleled luxury living..."
                rows={4}
              />
            </div>
          </div>
          
          {/* Selling Points */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <Label>Selling Points</Label>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={addSellingPoint}
                type="button"
              >
                Add Selling Point
              </Button>
            </div>
            
            {projectOverviewContent.sellingPoints.map((point, index) => (
              <div key={index} className="border rounded-md p-4 mb-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-sm font-medium">Selling Point {index + 1}</h4>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => removeSellingPoint(index)}
                    className="h-8 text-red-500 hover:text-red-700"
                  >
                    Remove
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <Label htmlFor={`selling-point-icon-${index}`}>Icon</Label>
                    <Select 
                      value={point.icon} 
                      onValueChange={(value) => updateSellingPoint(index, 'icon', value)}
                    >
                      <SelectTrigger id={`selling-point-icon-${index}`}>
                        <SelectValue placeholder="Select icon" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="location">Location</SelectItem>
                        <SelectItem value="investment">Investment</SelectItem>
                        <SelectItem value="lifestyle">Lifestyle</SelectItem>
                        <SelectItem value="design">Design</SelectItem>
                        <SelectItem value="security">Security</SelectItem>
                        <SelectItem value="amenities">Amenities</SelectItem>
                        <SelectItem value="view">View</SelectItem>
                        <SelectItem value="sustainability">Sustainability</SelectItem>
                        <SelectItem value="technology">Technology</SelectItem>
                        <SelectItem value="community">Community</SelectItem>
                        <SelectItem value="luxury">Luxury</SelectItem>
                        <SelectItem value="accessibility">Accessibility</SelectItem>
                        <SelectItem value="transport">Transport</SelectItem>
                        <SelectItem value="payment">Payment Plan</SelectItem>
                        <SelectItem value="pool">Pool</SelectItem>
                        <SelectItem value="gym">Gym</SelectItem>
                        <SelectItem value="garden">Garden</SelectItem>
                        <SelectItem value="parking">Parking</SelectItem>
                        <SelectItem value="playground">Playground</SelectItem>
                        <SelectItem value="restaurant">Restaurant</SelectItem>
                        <SelectItem value="spa">Spa</SelectItem>
                        <SelectItem value="building">Building</SelectItem>
                        <SelectItem value="calendar">Calendar</SelectItem>
                        <SelectItem value="clock">Clock</SelectItem>
                        <SelectItem value="money">Money</SelectItem>
                        <SelectItem value="shield">Shield</SelectItem>
                        <SelectItem value="star">Star</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor={`selling-point-title-${index}`}>Title</Label>
                    <Input
                      id={`selling-point-title-${index}`}
                      value={point.title}
                      onChange={(e) => updateSellingPoint(index, 'title', e.target.value)}
                      placeholder="Strategic Location"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor={`selling-point-description-${index}`}>Description</Label>
                    <Textarea
                      id={`selling-point-description-${index}`}
                      value={point.description}
                      onChange={(e) => updateSellingPoint(index, 'description', e.target.value)}
                      placeholder="Minutes away from Dubai Mall, Burj Khalifa..."
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            ))}
            
            {projectOverviewContent.sellingPoints.length === 0 && (
              <div className="text-center p-4 border border-dashed rounded-md">
                <p className="text-sm text-gray-500">No selling points added yet. Click the button above to add one.</p>
              </div>
            )}
          </div>
          
          {/* Unit Types */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <Label>Unit Types</Label>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={addUnitType}
                type="button"
              >
                Add Unit Type
              </Button>
            </div>
            
            {projectOverviewContent.unitTypes.map((unit, index) => (
              <div key={index} className="border rounded-md p-4 mb-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-sm font-medium">Unit Type {index + 1}</h4>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => removeUnitType(index)}
                    className="h-8 text-red-500 hover:text-red-700"
                  >
                    Remove
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <Label htmlFor={`unit-type-type-${index}`}>Type</Label>
                    <Input
                      id={`unit-type-type-${index}`}
                      value={unit.type}
                      onChange={(e) => updateUnitType(index, 'type', e.target.value)}
                      placeholder="1 Bedroom Apartment"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor={`unit-type-size-${index}`}>Size</Label>
                    <Input
                      id={`unit-type-size-${index}`}
                      value={unit.size}
                      onChange={(e) => updateUnitType(index, 'size', e.target.value)}
                      placeholder="850-1,100 sq.ft"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor={`unit-type-price-${index}`}>Starting Price</Label>
                    <Input
                      id={`unit-type-price-${index}`}
                      value={unit.startingPrice}
                      onChange={(e) => updateUnitType(index, 'startingPrice', e.target.value)}
                      placeholder="AED 1.8M"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor={`unit-type-image-${index}`}>Unit Image</Label>
                    {projectId && (
                      <ImageUploader
                        projectId={projectId as Id<"projects">}
                        componentId={projectOverviewComponent?._id}
                        purpose={`unit-type-${index}`}
                        altText={`${unit.type} image`}
                        existingImageUrl={unit.image}
                        existingFileId={unit.imageId}
                        onUploadComplete={(fileId, url) => handleUnitTypeImageChange(index, fileId, url)}
                        maxSizeMB={10}
                        acceptedTypes={['image/jpeg', 'image/png', 'image/webp']}
                        enableCompression={true}
                        compressionThresholdKB={200}
                        showHelper={index === 0}
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {projectOverviewContent.unitTypes.length === 0 && (
              <div className="text-center p-4 border border-dashed rounded-md">
                <p className="text-sm text-gray-500">No unit types added yet. Click the button above to add one.</p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button
          variant="outline"
          onClick={resetToDefault}
          disabled={isLoading}
        >
          Reset
        </Button>
        <Button 
          onClick={handleSaveProjectOverviewComponent}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : 'Save Project Overview'}
        </Button>
      </CardFooter>
    </Card>
  );
}; 